import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight, AlertCircle, Mail } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useDiagnosticContext } from '../context/DiagnosticContext';

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'pending'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const { refreshProfile, profile } = useAuth();
  const navigate = useNavigate();
  const { resetDiagnostic } = useDiagnosticContext();
  const MAX_ATTEMPTS = 20; // ~60s at 3s interval before surfacing the support fallback
  const verifyPayment = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      setErrorMessage('Missing payment session ID.');
      return;
    }

    try {
      const response = await fetch(`/api/verify-session?session_id=${sessionId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      if (data.status === 'success') {
        await refreshProfile();
        setStatus('success');
      } else if (data.status === 'pending') {
        setStatus('pending');
      } else {
        throw new Error('Unexpected status from verification server');
      }
    } catch (err: any) {
      console.error('Payment verification error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'An error occurred while confirming your payment.');
    }
  }, [sessionId, refreshProfile]);

  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);

  // If pending, retry after a few seconds. The Stripe webhook is the single
  // source of truth for credit activation; we poll until it finishes or give
  // up and surface the support fallback after MAX_ATTEMPTS.
  useEffect(() => {
    if (status !== 'pending') return;
    if (attempts >= MAX_ATTEMPTS) {
      setStatus('error');
      setErrorMessage('Webhook activation is taking longer than expected. Please contact support.');
      return;
    }
    const timer = setTimeout(() => {
      setAttempts(a => a + 1);
      verifyPayment();
    }, 3000);
    return () => clearTimeout(timer);
  }, [status, attempts, verifyPayment]);

  const remainingCredits = profile?.credits || 0;

  return (
    <div className="app">
      <Header />
      <main style={{ padding: '100px 0', textAlign: 'center', background: 'var(--bg-1)', minHeight: '75vh' }}>
        <div className="wrap" style={{ maxWidth: '600px' }}>
          
          {status === 'verifying' || status === 'pending' ? (
            <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
              <Loader2 size={64} className="spinning" style={{ color: 'var(--accent)', marginBottom: '24px' }} />
              <h1 style={{ fontSize: '2rem', fontWeight: 800 }}>Confirming Payment...</h1>
              <p style={{ color: 'var(--text-3)', fontSize: '1.1rem' }}>
                {status === 'pending' 
                  ? "Stripe is still processing the transaction. We'll refresh automatically..." 
                  : "We're activating your report credits. This will only take a moment."}
              </p>
              {sessionId && (
                <div style={{ marginTop: '24px', padding: '12px', background: 'var(--bg-2)', borderRadius: '12px', fontSize: '0.8rem', color: 'var(--text-3)', border: '1px solid var(--border)' }}>
                  Session: {sessionId}
                </div>
              )}
            </div>
          ) : status === 'success' ? (
            <div style={{ animation: 'scaleUp 0.5s ease-out' }}>
              <CheckCircle2 size={72} style={{ color: 'var(--accent)', marginBottom: '24px' }} />
              <h1 style={{ fontSize: '2.5rem', fontWeight: 900, marginBottom: '16px' }}>Payment Successful!</h1>
              <p style={{ color: 'var(--text-2)', fontSize: '1.25rem', marginBottom: '40px', lineHeight: 1.6 }}>
                Your credits are active. You now have <strong style={{ color: 'var(--accent)', fontSize: '1.5rem' }}>{remainingCredits}</strong> report {remainingCredits === 1 ? 'credit' : 'credits'} available.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="btn btn-primary"
                  style={{ width: '100%', maxWidth: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '18px' }}
                >
                  Go to Dashboard <ArrowRight size={20} />
                </button>
                <button 
                  onClick={() => { resetDiagnostic(); navigate('/diagnostic'); }}
                  style={{ background: 'none', border: 'none', color: 'var(--text-3)', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}
                >
                  Or start a new scan now &rarr;
                </button>
              </div>
            </div>
          ) : (
            <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
              <AlertCircle size={72} style={{ color: '#ef4444', marginBottom: '24px' }} />
              <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '16px' }}>Verification Delayed</h1>
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(239, 68, 68, 0.2)', marginBottom: '32px', textAlign: 'left' }}>
                <p style={{ color: 'var(--text-1)', marginBottom: '12px', fontWeight: 600 }}>Payment received but credits were not activated automatically.</p>
                <p style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>Reason: {errorMessage || 'Connection timeout or metadata mismatch.'}</p>
              </div>
              
              <p style={{ color: 'var(--text-2)', marginBottom: '32px' }}>
                Don't worry! Your payment is secure. Please contact our support team with your Payment ID and we will manually activate your credits immediately.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <a 
                  href={`mailto:support@pccheckr.com?subject=Payment Issue: ${sessionId}`}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '16px', border: '1px solid #ef4444', color: '#ef4444' }}
                >
                  <Mail size={18} /> Contact Support
                </a>
                <button 
                  onClick={() => window.location.reload()}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontWeight: 600 }}
                >
                  Try refreshing the page
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <style>{`
        @keyframes scaleUp {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
